import type {
  AnalyzeWebsiteResponse,
  WebsiteAnalysisSnapshot,
} from '@borradh-workspace/api-client/types';

/**
 * Union of the two shapes that feed this helper: what the LIVE SCANNER
 * returns (`AnalyzeWebsiteResponse`) and what the OPERATOR UPLOAD returns
 * (`WebsiteAnalysisSnapshot`). They overlap on every field this helper reads
 * — services, brand, hours, locations, practitioners — but the snapshot is
 * more forgiving (every field is optional). Both types are re-exported
 * from api-client so a caller can pass whichever it holds.
 */
export type WebsiteAnalysisInput =
  | AnalyzeWebsiteResponse
  | WebsiteAnalysisSnapshot;

// Opening-hours snap constants — must move in lockstep with the Select grid
// in step-7-opening-hours.
const MIN_TIME = 360; // 6:00 AM
const MAX_TIME = 1380; // 11:00 PM
const INTERVAL = 30;

const snap = (m: number) =>
  Math.max(MIN_TIME, Math.min(MAX_TIME, Math.round(m / INTERVAL) * INTERVAL));

/**
 * Map a completed website-analysis snapshot onto the onboarding form.
 *
 * Called from TWO places that must stay behaviourally identical:
 *
 *   1. The scanner path — `onBeforeContinue` on the website-scanner step,
 *      once the async analyze job resolves.
 *   2. The operator-upload path — after `POST /website-analysis/validate-snapshot`
 *      returns a snapshot that an operator produced off-app with Claude Code.
 *
 * Extracted so both paths write the SAME fields the SAME way. When a scan and
 * an upload diverge, this is where the difference lives — nowhere else.
 *
 * NOTE: pricing amounts in the snapshot are in whole local-currency units
 * (150 for "£150"); the service model stores CENTS. That conversion happens
 * here, and any caller that reaches into `serviceDetails` afterwards must
 * respect it.
 */
// biome-ignore lint/suspicious/noExplicitAny: The wizard form's full schema lives in onboarding-form.tsx and isn't exported; typing this as `UseFormReturn<OnboardingFormData>` would create a circular import.
export function applySnapshotToForm(form: any, result: WebsiteAnalysisInput) {
  // ── Services + per-service structured price ─────────────────────────────
  if (result.services?.length) {
    const serviceNames = result.services.map((s) => s.name);
    form.setValue('services', serviceNames, { shouldDirty: true });

    const currentCategories = form.getValues('serviceCategories') || {};
    const updatedCategories = { ...currentCategories };
    for (const service of result.services) {
      updatedCategories[service.name] =
        service.categoryName?.trim() || 'Uncategorised';
    }
    form.setValue('serviceCategories', updatedCategories, {
      shouldDirty: true,
    });

    const currentDetails = form.getValues('serviceDetails') || {};
    const updatedDetails = { ...currentDetails };
    for (const svc of result.services) {
      const priceCents =
        svc.priceAmount === undefined
          ? undefined
          : Math.round(svc.priceAmount * 100);
      const existing = updatedDetails[svc.name];
      // The scanned description is what the writer stores on the service row.
      const description = svc.description?.trim().slice(0, 5000) || undefined;
      if (!existing) {
        updatedDetails[svc.name] = {
          appointmentDuration: 30,
          ...(description && { description }),
          requiresDeposit: false,
          priceType: svc.priceType,
          priceCents,
        };
      } else {
        if (svc.priceType && !existing.priceType) {
          updatedDetails[svc.name] = {
            ...existing,
            priceType: svc.priceType,
            priceCents,
          };
        }
        if (description && !existing.description) {
          updatedDetails[svc.name] = {
            ...updatedDetails[svc.name],
            description,
          };
        }
      }
    }
    form.setValue('serviceDetails', updatedDetails, { shouldDirty: true });
  }

  // ── Brand tone / audience / credibility ────────────────────────────────
  if (result.targetAudienceDescription) {
    form.setValue(
      'targetAudienceDescription',
      result.targetAudienceDescription,
      { shouldDirty: true }
    );
  }
  if (result.brandVoice?.length) {
    form.setValue('brandVoice', result.brandVoice, { shouldDirty: true });
  }
  if (result.suggestedCredibilityLines?.length) {
    form.setValue(
      'suggestedCredibilityLines',
      result.suggestedCredibilityLines,
      { shouldDirty: true }
    );
  }

  // ── Brand colours ───────────────────────────────────────────────────────
  if (result.primaryColor) {
    form.setValue('primaryColor', result.primaryColor, { shouldDirty: true });
  }
  if (result.secondaryColor) {
    form.setValue('secondaryColor', result.secondaryColor, {
      shouldDirty: true,
    });
  }

  // ── Locations ───────────────────────────────────────────────────────────
  if (result.locations?.length) {
    const mappedLocations = result.locations.map((loc) => ({
      id: crypto.randomUUID(),
      name: loc.name || '',
      phone: loc.phone || '',
      addressLine1: loc.addressLine1,
      addressLine2: '',
      city: loc.city,
      county: loc.county || '',
      postalCode: loc.postalCode || '',
      country: loc.country,
      isPrimary: false,
      latitude: loc.latitude ?? null,
      longitude: loc.longitude ?? null,
    }));
    form.setValue('locations', mappedLocations, { shouldDirty: true });
  }

  // ── Business hours (snapped to the UI's 30-min grid) ────────────────────
  if (result.businessHours && Object.keys(result.businessHours).length > 0) {
    const snapped: Record<string, { from: number; to: number }> = {};
    for (const [day, hours] of Object.entries(result.businessHours)) {
      const h = hours as { from: number; to: number };
      const from = snap(h.from);
      let to = snap(h.to);
      if (to <= from) to = Math.min(from + INTERVAL, MAX_TIME);
      snapped[day] = { from, to };
    }
    form.setValue('businessHours', snapped, { shouldDirty: true });
  }

  // ── Step-3 URL fields + booking-system choice (operator-upload only) ────
  // The scanner never emits these; the operator snapshot does. `in`-guarded
  // so the union with `AnalyzeWebsiteResponse` (which lacks them) narrows
  // safely without a cast. Present-and-truthy check means an operator can
  // include some of these and skip the rest without erasing existing values.
  if ('facebookPageUrl' in result && result.facebookPageUrl) {
    form.setValue('facebookPageUrl', result.facebookPageUrl, {
      shouldDirty: true,
    });
  }
  if ('bookingSystemUrl' in result && result.bookingSystemUrl) {
    form.setValue('bookingSystemUrl', result.bookingSystemUrl, {
      shouldDirty: true,
    });
  }
  if ('bookingLink' in result && result.bookingLink) {
    form.setValue('bookingLink', result.bookingLink, { shouldDirty: true });
  }
  if ('bookingSystemChoice' in result && result.bookingSystemChoice) {
    form.setValue('bookingSystemChoice', result.bookingSystemChoice, {
      shouldDirty: true,
    });
  }

  // ── Team ────────────────────────────────────────────────────────────────
  if (result.practitioners?.length) {
    const existingPractitioners = form.getValues('practitioners') || [];
    const existingNames = new Set(
      existingPractitioners.map((p: { name: string }) =>
        p.name.toLowerCase().trim()
      )
    );
    const newPractitioners = result.practitioners
      .filter(
        (p: { name: string }) => !existingNames.has(p.name.toLowerCase().trim())
      )
      .map((p: { name: string; title?: string }) => ({
        id: crypto.randomUUID(),
        name: p.name,
        email: '',
        title: p.title || undefined,
      }));
    form.setValue(
      'practitioners',
      [...existingPractitioners, ...newPractitioners],
      { shouldDirty: true }
    );
  }
}
